/* Secure channel — browser side. The SAME file lives in
   frontend/src/apiservices/secureChannel.js and admin/src/services/secureChannel.js;
   change both together. Server side: server/services/secureChannelService.js.
   Plan: docs/plans/2026-10-14-api-response-encryption.md

   When the server runs with API_ENCRYPTION=on, every API response and socket
   message arrives AES-256-GCM encrypted, so the Network tab shows binary. This
   module does the key exchange and turns it back into the normal JSON before
   any page code sees it — pages and services do not change.

   With encryption off the handshake answers { enabled: false } and everything
   here is a pass-through, so local development stays plain JSON.

   No imports on purpose: server/__tests__ load this file in Node to prove the
   browser and the server agree on the format. */

const subtle = globalThis.crypto?.subtle;
const enc = new TextEncoder();
const dec = new TextDecoder();
const INFO = enc.encode('tenexch-cs-v1');

const b64 = (bytes) => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};
const unb64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
const toBytes = (data) => (data instanceof Uint8Array ? data
  : ArrayBuffer.isView(data) ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    : new Uint8Array(data));
const isBinary = (v) => v instanceof ArrayBuffer || ArrayBuffer.isView(v);

export const canInflate = (() => {
  try { new DecompressionStream('deflate-raw'); return true; } catch { return false; }
})();

const inflateRaw = async (bytes) => {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

// ── Crypto primitives (exported for the server-side compatibility test) ──────

export const createClientKeys = async () => {
  const kp = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  const pub = new Uint8Array(await subtle.exportKey('raw', kp.publicKey));
  return { kp, pub: b64(pub) };
};

export const deriveSessionKey = async (kp, serverPub, sid) => {
  const peer = await subtle.importKey('raw', unb64(serverPub), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  const secret = await subtle.deriveBits({ name: 'ECDH', public: peer }, kp.privateKey, 256);
  const ikm = await subtle.importKey('raw', secret, 'HKDF', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode(sid), info: INFO },
    ikm, { name: 'AES-GCM', length: 256 }, false, ['decrypt'],
  );
};

const importAesKey = (raw) => subtle.importKey('raw', unb64(raw), 'AES-GCM', false, ['decrypt']);

// Frame -> JSON value. Session frame [1][flags][iv][ct+tag]; broadcast frame
// [2][flags][keyId u32][iv][ct+tag]. The header is the GCM additional data.
export const openFrame = async (data, sessionKey, broadcastKey) => {
  const bytes = toBytes(data);
  let headerLen, key;
  if (bytes[0] === 1) {
    headerLen = 2;
    key = sessionKey;
  } else if (bytes[0] === 2) {
    headerLen = 6;
    key = broadcastKey?.(new DataView(bytes.buffer, bytes.byteOffset + 2, 4).getUint32(0));
  } else {
    throw new Error('secure channel: unknown frame');
  }
  if (!key) throw new Error('secure channel: no key for frame');
  const plain = new Uint8Array(await subtle.decrypt(
    { name: 'AES-GCM', iv: bytes.subarray(headerLen, headerLen + 12), additionalData: bytes.subarray(0, headerLen) },
    key, bytes.subarray(headerLen + 12),
  ));
  const raw = bytes[1] & 1 ? await inflateRaw(plain) : plain;
  return JSON.parse(dec.decode(raw));
};

// ── Session ─────────────────────────────────────────────────────────────────

export const createSecureChannel = ({ handshakeUrl }) => {
  let state = null; // { enabled:false } | { enabled:true, sid, key }
  let pending = null;
  const broadcastKeys = new Map(); // keyId -> CryptoKey, shared by every socket of this tab

  // WebCrypto exists only on https / localhost; without it the channel stays
  // off (local LAN development over plain http).
  const ensureSession = (force = false) => {
    if (!subtle) return Promise.resolve({ enabled: false });
    if (state && !force) return Promise.resolve(state);
    if (pending) return pending;
    pending = (async () => {
      const { kp, pub } = await createClientKeys();
      const res = await fetch(handshakeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pub, inflate: canInflate }),
      });
      const body = await res.json();
      state = body.enabled
        ? { enabled: true, sid: body.sid, key: await deriveSessionKey(kp, body.pub, body.sid) }
        : { enabled: false };
      return state;
    })().finally(() => { pending = null; });
    return pending;
  };

  const decodeBody = async (response) => {
    const bytes = toBytes(response.data);
    if (String(response.headers?.['x-enc']) === '1') return openFrame(bytes, state?.key);
    const text = dec.decode(bytes);
    try { return text ? JSON.parse(text) : text; } catch { return text; }
  };

  // Adds the session header, reads bodies as bytes and decrypts them. Install
  // AFTER the instance's own interceptors so they still see the raw response,
  // while every caller receives the decoded JSON.
  const installHttp = (instance) => {
    instance.interceptors.request.use(async (config) => {
      const s = await ensureSession().catch(() => null);
      if (s?.enabled) {
        config.headers['X-CS-Id'] = s.sid;
        if (!config.responseType) {
          config.responseType = 'arraybuffer';
          config._cs = true;
        }
      }
      return config;
    });
    instance.interceptors.response.use(
      async (response) => {
        if (response.config?._cs) response.data = await decodeBody(response);
        return response;
      },
      async (error) => {
        const { config, response } = error;
        if (response && config?._cs) {
          response.data = await decodeBody(response).catch(() => response.data);
        }
        // Session unknown/expired (or the server switched encryption on after
        // this tab opened): the server did NOT run the route, so one retry after
        // a fresh handshake is always safe (no double bets).
        if (response?.status === 409 && response.data?.code === 'CS_REHANDSHAKE' && config && !config._csRetried) {
          await ensureSession(true);
          return instance.request({
            ...config, _csRetried: true, _cs: false, responseType: config._cs ? undefined : config.responseType,
          });
        }
        return Promise.reject(error);
      },
    );
    return instance;
  };

  // socket.io `auth` option: adds the session id at every (re)connect.
  const socketAuth = (extra = {}) => (cb) => {
    ensureSession()
      .then((s) => cb({ ...extra, ...(s?.enabled ? { sid: s.sid } : {}) }))
      .catch(() => cb(extra));
  };

  // Decrypts every incoming event before the app's listeners run, keeping the
  // order the server sent them in. Call once, right after io(...).
  const RESERVED = new Set(['connect', 'disconnect', 'connect_error', 'error']);
  const secureSocket = (socket) => {
    const on = socket.on.bind(socket);
    const off = socket.off.bind(socket);
    const wrappers = new Map(); // listener -> Map(event -> wrapper)
    let chain = Promise.resolve();
    const later = (task) => { chain = chain.then(task).catch((e) => console.error('[secure socket]', e)); };

    on('cs:bk', (frame) => later(async () => {
      const { keys } = await openFrame(frame, state?.key);
      for (const k of keys) {
        if (!broadcastKeys.has(k.id)) broadcastKeys.set(k.id, await importAesKey(k.k));
      }
    }));
    on('connect_error', (err) => {
      if (err?.message === 'CS_REHANDSHAKE') ensureSession(true).then(() => socket.connect()).catch(() => {});
    });

    socket.on = (event, listener) => {
      if (RESERVED.has(event)) return on(event, listener);
      const wrapper = (...args) => later(async () => {
        const plain = await Promise.all(args.map((a) => (isBinary(a)
          ? openFrame(a, state?.key, (id) => broadcastKeys.get(id)) : a)));
        listener(...plain);
      });
      if (!wrappers.has(listener)) wrappers.set(listener, new Map());
      wrappers.get(listener).set(event, wrapper);
      return on(event, wrapper);
    };
    socket.off = (event, listener) => {
      if (!listener) return off(event);
      const wrapper = wrappers.get(listener)?.get(event);
      wrappers.get(listener)?.delete(event);
      return off(event, wrapper || listener);
    };
    return socket;
  };

  return { ensureSession, installHttp, socketAuth, secureSocket };
};
